import React, { useRef, useState, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';

const ParticleSwarm = ({ isHovered, mousePos, color }) => {
    const pointsRef = useRef();
    const count = 300;

    const particles = useMemo(() => {
        const positions = new Float32Array(count * 3);
        const velocities = [];
        for (let i = 0; i < count; i++) {
            positions[i * 3] = (Math.random() - 0.5) * 4;
            positions[i * 3 + 1] = (Math.random() - 0.5) * 4;
            positions[i * 3 + 2] = (Math.random() - 0.5) * 2;
            velocities.push({
                x: (Math.random() - 0.5) * 0.02,
                y: (Math.random() - 0.5) * 0.02,
                z: (Math.random() - 0.5) * 0.02
            });
        }
        return { positions, velocities };
    }, [count]);

    useFrame((state) => {
        if (!pointsRef.current) return;

        const positions = pointsRef.current.geometry.attributes.position.array;
        pointsRef.current.rotation.y += 0.002;
        pointsRef.current.rotation.x += 0.001;
        const targetX = isHovered ? mousePos.current.x * 2.5 : 0;
        const targetY = isHovered ? -mousePos.current.y * 2.5 : 0;

        for (let i = 0; i < count; i++) {
            let px = positions[i * 3];
            let py = positions[i * 3 + 1];
            let pz = positions[i * 3 + 2];

            if (isHovered) {
                const dx = targetX - px;
                const dy = targetY - py;
                const dz = 0 - pz;
                const dist = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
                const force = Math.max(0.1, 1 / dist) * 0.01;
                particles.velocities[i].x += dx * force * 0.05;
                particles.velocities[i].y += dy * force * 0.05;
                particles.velocities[i].z += dz * force * 0.05;
                particles.velocities[i].x *= 0.88;
                particles.velocities[i].y *= 0.88;
                particles.velocities[i].z *= 0.88;
            } else {
                const idx = -px * 0.001;
                const idy = -py * 0.001;
                particles.velocities[i].x += idx;
                particles.velocities[i].y += idy;
                particles.velocities[i].x *= 0.98;
                particles.velocities[i].y *= 0.98;
                particles.velocities[i].z *= 0.98;
            }
            positions[i * 3] += particles.velocities[i].x;
            positions[i * 3 + 1] += particles.velocities[i].y;
            positions[i * 3 + 2] += particles.velocities[i].z;
        }
        pointsRef.current.geometry.attributes.position.needsUpdate = true;
        const targetOpacity = isHovered ? 0.8 : 0.0;
        pointsRef.current.material.opacity += (targetOpacity - pointsRef.current.material.opacity) * 0.1;
    });
    return (
        <points ref={pointsRef}>
            <bufferGeometry>
                <bufferAttribute
                    attach="attributes-position"
                    count={count}
                    array={particles.positions}
                    itemSize={3}
                />
            </bufferGeometry>
            <pointsMaterial
                size={0.06}
                color={color || "#76AB3F"}
                transparent
                opacity={0}
                sizeAttenuation={true}
                depthWrite={false}
            />
        </points>
    );
};

export default function Hover3DCard({ children, color, style }) {
    const cardRef = useRef(null);
    const [isHovered, setIsHovered] = useState(false);
    const normalizedMouse = useRef({ x: 0, y: 0 });
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const smoothMx = useSpring(mx, { stiffness: 300, damping: 20 });
    const smoothMy = useSpring(my, { stiffness: 300, damping: 20 });
    const rotateX = useTransform(smoothMy, [-1, 1], [10, -10]);
    const rotateY = useTransform(smoothMx, [-1, 1], [-10, 10]);
    const handleMouseMove = (e) => {
        if (!cardRef.current) return;
        const rect = cardRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        const nx = x / (rect.width / 2);
        const ny = y / (rect.height / 2);
        normalizedMouse.current = { x: nx, y: ny };
        mx.set(nx);
        my.set(ny);
    };
    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => {
        setIsHovered(false);
        mx.set(0);
        my.set(0);
        normalizedMouse.current = { x: 0, y: 0 };
    };
    return (
        <motion.div
            ref={cardRef}
            onMouseMove={handleMouseMove}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{
                position: 'relative',
                perspective: 1000,
                transformStyle: 'preserve-3d',
                cursor: 'pointer',
                ...style
            }}
            initial={false}
            animate={{
                scale: isHovered ? 1.02 : 1,
            }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
        >
            <motion.div
                style={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    rotateX,
                    rotateY,
                    transformStyle: 'preserve-3d',
                }}
                animate={{ z: isHovered ? 20 : 0 }}
            >
                <div style={{
                    position: 'absolute',
                    inset: -10,
                    zIndex: 0,
                    borderRadius: style?.borderRadius || 10,
                    overflow: 'hidden',
                    background: 'transparent'
                }}>
                    <Canvas
                        camera={{ position: [0, 0, 3], fov: 45 }}
                        style={{ pointerEvents: 'none' }}
                        gl={{ alpha: true, antialias: false }}
                    >
                        <ParticleSwarm isHovered={isHovered} mousePos={normalizedMouse} color={color} />
                    </Canvas>
                </div>
                <div style={{
                    position: 'relative',
                    zIndex: 1,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(255, 255, 255, 0.65)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255, 255, 255, 0.4)',
                    boxShadow: isHovered
                        ? '0 15px 35px rgba(0,0,0,0.1), 0 0 20px rgba(118, 171, 63, 0.2)'
                        : '0 4px 15px rgba(0,0,0,0.05)',
                    borderRadius: style?.borderRadius || 10,
                    transition: 'box-shadow 0.3s ease',
                }}>
                    <motion.div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            borderRadius: 'inherit',
                            background: `radial-gradient(circle at ${isHovered ? (normalizedMouse.current.x * 50 + 50) : 50}% ${isHovered ? (normalizedMouse.current.y * 50 + 50) : 50}%, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 60%)`,
                            opacity: isHovered ? 0.4 : 0,
                            pointerEvents: 'none',
                            mixBlendMode: 'overlay',
                            transition: 'opacity 0.4s ease'
                        }}
                    />
                    {children}
                </div>
            </motion.div>
        </motion.div>
    );
}
